import React from 'react';
import { useDispatch } from 'react-redux';
import {
	Button,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	ImageList,
	ImageListItem,
	InputAdornment,
	styled,
	TextField
} from '@mui/material';

import { closeDialog, openDialog } from 'app/store/fuse/dialogSlice';
import useSettings from '../hooks/useSettings';
import { executeApi } from '../Utils';
import { grabLogoImage } from '../files-explorer/FilesUtils';
import Icon from '../Icon';

interface LogoGrabberDialogProps {
	fieldName: string;
	fileName: string;
	path: string;
	defaultSearch?: string;
	sendFilesChange: (fieldName: string, path: string, renamedImage: File) => void;
	setUploadedImage: (newImage: string | null) => void;
}

const Image = styled('img')(({ theme }) => ({
	width: '100%',
	'&.Image-selected': {
		padding: theme.spacing(1),
		borderRadius: 6,
		backgroundColor: theme.palette.secondary.main
	}
}));

const Overlay = styled('div')(({ theme }) => ({
	position: 'absolute',
	width: '100%',
	height: '100%',
	top: 0,
	right: 0,
	'&:hover': {
		backgroundColor: 'rgb(0, 0, 0, 0.25)'
	}
}));

export const showLogoGrabberDialog = (props: LogoGrabberDialogProps) =>
	openDialog({
		children: <LogoGrabberDialog {...props} />,
		maxWidth: 'sm',
		fullWidth: true,
		onClose: () => {}
	});

export default function LogoGrabberDialog({
	fieldName,
	fileName,
	path,
	defaultSearch,
	sendFilesChange,
	setUploadedImage
}: LogoGrabberDialogProps) {
	const dispatch = useDispatch();
	const { sysSettings, t } = useSettings();

	const [selectedImageUrl, setSelectedImageUrl] = React.useState<string | undefined>();

	const handleAvatarChange = image => {
		const type = image.type.split('/')[1];
		const renamedImage = new File([image], `${fileName}.${type}`, { type: image.type });
		sendFilesChange(fieldName, `${sysSettings.files_path}\\${path}`, renamedImage);

		const reader = new FileReader();
		reader.addEventListener('load', () => setUploadedImage(reader.result as string));
		reader.readAsDataURL(renamedImage);

		dispatch(closeDialog());
	};

	const grabImage = () => {
		executeApi.post({
			url: '/api/filexchange/logo/download',
			body: { Url: selectedImageUrl },
			dispatch,
			successHandler: response => {
				const rawData = atob(response.data.image_data);
				const imgData = new Uint8Array(rawData.length);
				for (let i = 0; i < rawData.length; i += 1) {
					imgData[i] = rawData.charCodeAt(i);
				}
				const file = new File([imgData], `logo.${response.data.image_type}`, {
					type: `image/${response.data.image_type}`
				});
				handleAvatarChange(file);
			}
		});
	};

	return (
		<>
			<DialogTitle>{t('DIALOG_LOGO_TITLE')}</DialogTitle>
			<DialogContent className="pb-0">
				<PickerComponent defaultSearch={defaultSearch} setSelectedImageUrl={setSelectedImageUrl} />
			</DialogContent>
			<DialogActions>
				<Button onClick={() => dispatch(closeDialog())}>{t('DIALOG_ACTION_CANCEL')}</Button>
				<Button onClick={grabImage}>{t('DIALOG_ACTION_CONFIRM')}</Button>
			</DialogActions>
		</>
	);
}

function PickerComponent({
	defaultSearch,
	setSelectedImageUrl
}: {
	defaultSearch?: string;
	setSelectedImageUrl: (newUrl?: string) => void;
}) {
	const dispatch = useDispatch();
	const { t } = useSettings();

	const [images, setImages] = React.useState<any[]>([]);
	const [selectedItem, setSelectedItem] = React.useState(-1);
	const [queryStringStore, setQueryStringStore] = React.useState(defaultSearch);
	const [queryString, setQueryString] = React.useState(defaultSearch);

	React.useEffect(() => {
		grabLogoImage({
			url: queryString || '',
			dispatch,
			successHandler: response => {
				setImages(response.data);
				setSelectedItem(-1);
				setSelectedImageUrl(undefined);
			}
		});
	}, [dispatch, queryString, setSelectedImageUrl]);

	const changeSelectedItem = (key, url) => {
		setSelectedItem(key);
		setSelectedImageUrl(url);
	};

	const handleKeyDown = event => {
		if (event.key === 'Enter') {
			event.preventDefault();
			setQueryString(queryStringStore);
		}
	};

	return (
		<Grid container direction="column" spacing={1}>
			<Grid>
				<TextField
					variant="outlined"
					size="small"
					fullWidth
					placeholder={t('SEARCH_PLACEHOLDER')}
					value={queryStringStore}
					onChange={e => setQueryStringStore(e.target.value)}
					margin="none"
					autoComplete="off"
					onKeyDown={handleKeyDown}
					InputProps={{
						classes: { root: 'p-0' },
						endAdornment: (
							<InputAdornment position="end">
								<IconButton className="p-8" onClick={() => setQueryStringStore('')}>
									<Icon icon="close" />
								</IconButton>
								<IconButton className="p-8" onClick={() => setQueryString(queryStringStore)}>
									<Icon icon="magnify" />
								</IconButton>
							</InputAdornment>
						)
					}}
				/>
			</Grid>
			<Grid className="h-full w-full overflow-y-scroll">
				<ImageList variant="masonry" cols={3} gap={8}>
					{images &&
						images.map((imageUrl, i) => (
							<ImageListItem key={i} className="w-full" onClick={() => changeSelectedItem(i, imageUrl)}>
								<Image
									className={selectedItem === i ? 'Image-selected' : undefined}
									src={imageUrl}
									alt={imageUrl}
									loading="lazy"
								/>
								<Overlay />
							</ImageListItem>
						))}
				</ImageList>
			</Grid>
		</Grid>
	);
}
